/**
 * Copies a file the person just picked into memory, and hands back a `File` backed by those bytes.
 *
 * Why it exists: on Android, Chrome (and Samsung Internet) does not read the picked file when the
 * person picks it. It keeps a reference to the document (a `content://` URI from Drive, WhatsApp,
 * Downloads, a cloud provider) and reads it only when the request body is sent. If by then the
 * provider answers differently, or the grant has lapsed, the browser aborts the request before a
 * single byte leaves the phone. The server never sees it, the request ends with status 0, and the
 * person sees nothing at all.
 *
 * Measured on production RUM between 10/09 and 30/09/2026: every `POST /workers/upload_cv` that
 * ended with status 0 came from Android, never iOS or desktop; on the profile page it was up to
 * half of the attempts on a bad day. The CORS preflight of those attempts reached the API and was
 * answered 204, so it is not CORS: the browser had permission to send and failed to read the body.
 *
 * Reading the bytes at pick time moves that failure to the one moment we can do something about
 * it: if the phone cannot give us the file, we say so while the person is still choosing one,
 * instead of letting them press "Continua" on a file that will never be sent. Uses `FileReader`
 * rather than `Blob.arrayBuffer()` because it is available everywhere we run, jsdom included.
 */
export function readFileIntoMemory(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const bytes = reader.result;

      if (!(bytes instanceof ArrayBuffer)) {
        reject(new Error('readFileIntoMemory: the reader returned no bytes'));
        return;
      }

      resolve(new File([bytes], file.name, { type: file.type, lastModified: file.lastModified }));
    };

    reader.onerror = () => reject(reader.error ?? new Error('readFileIntoMemory: the file could not be read'));
    reader.onabort = () => reject(new Error('readFileIntoMemory: reading the file was aborted'));

    reader.readAsArrayBuffer(file);
  });
}
